import React from "react";
import {
  Plus,
  Save,
  Loader2,
  Navigation,
  Star,
  Search,
  LocateFixed,
} from "lucide-react";

const AddressForm = ({
  formData,
  setFormData,
  handleSubmit,
  handleUseCurrentLocation,
  handleAddressInputChange,
  handleSelectSuggestion,

  placeSuggestions,
  showSuggestions,
  setShowSuggestions,

  placesAutoCompletionLoading,
  resolvingLatLong,
  updateFavouriteAddressLoading,

  normalizeSuggestion,
}) => {
  return (
    <div className="rounded-[28px] bg-white p-5 shadow-[0_15px_40px_rgba(15,23,42,0.08)] md:p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <Plus size={22} />
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-900">Add Address</h2>
          <p className="text-sm text-slate-500">
            Type address and select from nearby suggestions
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Address Type
          </label>

          <div className="grid grid-cols-3 gap-2">
            {["Home", "Office", "Other"].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    addressType: item,
                  }))
                }
                className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                  formData.addressType === item
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Search Address
          </label>

          <div className="relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleAddressInputChange}
              onFocus={() => {
                if (placeSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              placeholder="Example: Sector 52 Noida"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-11 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
            />

            {(placesAutoCompletionLoading || resolvingLatLong) && (
              <Loader2
                size={18}
                className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-blue-600"
              />
            )}
          </div>

          {showSuggestions && (
            <div className="absolute left-0 right-0 top-[78px] z-50 max-h-[260px] overflow-y-auto rounded-2xl border border-slate-100 bg-white p-2 shadow-2xl">
              {placeSuggestions.length > 0 ? (
                placeSuggestions.map((item, index) => {
                  const suggestion = normalizeSuggestion(item);

                  return (
                    <button
                      type="button"
                      key={
                        item?.place_id ||
                        item?.placeId ||
                        item?.id ||
                        `${suggestion.displayAddress}-${index}`
                      }
                      onClick={() => handleSelectSuggestion(item)}
                      className="flex w-full gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-blue-50"
                    >
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                        <LocateFixed size={17} />
                      </span>

                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-800">
                          {suggestion.displayAddress || "Unnamed location"}
                        </span>

                        {suggestion.secondaryText && (
                          <span className="mt-0.5 block truncate text-xs text-slate-500">
                            {suggestion.secondaryText}
                          </span>
                        )}

                        {suggestion.latitude && suggestion.longitude && (
                          <span className="mt-1 block text-[11px] text-slate-400">
                            {suggestion.latitude}, {suggestion.longitude}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-5 text-center text-sm text-slate-500">
                  No nearby address found
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Latitude
            </label>

            <input
              type="text"
              name="latitude"
              value={formData.latitude}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  latitude: e.target.value,
                }))
              }
              placeholder="Auto filled"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Longitude
            </label>

            <input
              type="text"
              name="longitude"
              value={formData.longitude}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  longitude: e.target.value,
                }))
              }
              placeholder="Auto filled"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
        >
          <Navigation size={17} />
          Use Current Location
        </button>

        <button
          type="button"
          onClick={() =>
            setFormData((prev) => ({
              ...prev,
              isFavourite: !prev.isFavourite,
            }))
          }
          className={`flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
            formData.isFavourite
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          <Star
            size={17}
            className={formData.isFavourite ? "fill-white" : ""}
          />
          {formData.isFavourite ? "Marked as Favourite" : "Mark as Favourite"}
        </button>

        <button
          type="submit"
          disabled={updateFavouriteAddressLoading || resolvingLatLong}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {updateFavouriteAddressLoading || resolvingLatLong ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {resolvingLatLong ? "Getting Location..." : "Saving..."}
            </>
          ) : (
            <>
              <Save size={18} />
              Save Address
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default AddressForm;
